import { Text } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import useCurrentUser from 'src/admin/users/hooks/useCurrentUser';
import { ClientListItem } from 'src/admin/users/types';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import { SimpleTable } from 'src/common/components/table/SimpleTable';
import { SortableColumn } from 'src/common/components/table/types';
import { ClientActionsMenu } from './components/ClientActionsMenu';
import { matchesAdvisorSearch } from './components/AdvisorFilterSelect';
import { useAdvisors } from './hooks/useAdvisors';
import { useDeleteClient } from './hooks/useDeleteClient';
import { formatName, NameFormat } from 'src/utils/nameFormatting';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { formatShortDate } from 'src/utils/dateFormatting';

export function NewClientsTable({
  filters,
  searchTerm,
  paginated = false,
}: {
  filters?: Record<string, string>;
  searchTerm?: string;
  paginated?: boolean;
}) {
  const { advisors } = useAdvisors();
  const { isAdvisor } = useCurrentUser();
  const navigate = useNavigate();
  const [clientToDelete, setClientToDelete] = useState<ClientListItem | null>(null);
  const {
    deleteClient,
    error: deletionError,
    reset: resetDeleteClient,
    isLoading: isDeletingClient,
  } = useDeleteClient();

  const advisorMap = advisors
    ? Object.fromEntries(advisors.map(advisor => [advisor.id, advisor]))
    : {};

  const handleDeletePress = (client: ClientListItem) => {
    resetDeleteClient();
    setClientToDelete(client);
  };

  const handleDeleteConfirm = async () => {
    if (!clientToDelete) return;
    await deleteClient(clientToDelete.id);
    setClientToDelete(null);
  };

  const columnsBase: SortableColumn<ClientListItem>[] = [
    {
      accessor: 'name',
      title: 'Name',
      sortable: true,
      sortKey: 'lastName',
      sortIconType: 'alpha',
      render: (client: ClientListItem) => (
        <Text size="sm" fw={FONT_WEIGHTS.medium} c="black">
          {formatName(client.firstName, client.lastName, NameFormat.NATURAL)}
        </Text>
      ),
    },
    {
      accessor: 'family',
      title: 'Family',
      sortable: true,
      sortKey: 'family.name',
      sortIconType: 'alpha',
      render: (client: ClientListItem) => {
        const family = client.family;
        if (!family)
          return (
            <Text size="sm" fw={FONT_WEIGHTS.medium} c="black">
              —
            </Text>
          );
        return (
          <Text size="sm" fw={FONT_WEIGHTS.medium} c="black">
            {family.name}
          </Text>
        );
      },
    },
    {
      accessor: 'advisor',
      title: 'Adviser',
      sortable: true,
      sortIconType: 'alpha',
      sortValueGetter: (client: ClientListItem) => {
        if (!client.advisorUserId) return '';
        const advisor = advisorMap[client.advisorUserId];
        return advisor ? formatName(advisor.firstName, advisor.lastName, NameFormat.NATURAL) : '';
      },
      render: (client: ClientListItem) => {
        const advisorUserId = client.advisorUserId;
        if (!advisorUserId)
          return (
            <Text size="sm" fw={FONT_WEIGHTS.medium} c="black">
              No adviser
            </Text>
          );
        const advisor = advisorMap[advisorUserId];
        const advisorName = advisor
          ? formatName(advisor.firstName, advisor.lastName, NameFormat.NATURAL)
          : 'No adviser';
        return (
          <Text size="sm" fw={FONT_WEIGHTS.medium} c="black">
            {advisorName}
          </Text>
        );
      },
    },
    {
      accessor: 'createdAt',
      title: 'Last updated',
      sortable: true,
      sortKey: 'createdAt',
      sortIconType: 'date',
      render: (client: ClientListItem) => (
        <Text size="sm" fw={FONT_WEIGHTS.medium} c="black">
          {formatShortDate(client.createdAt)}
        </Text>
      ),
    },
    {
      accessor: 'actions',
      title: '',
      render: (client: ClientListItem) => (
        <ClientActionsMenu
          client={client}
          onDelete={() => handleDeletePress(client)}
          iconColor="var(--mantine-color-black)"
        />
      ),
    },
  ];

  const filterPredicate = (client: ClientListItem, search: string): boolean => {
    const term = search.toLowerCase();
    const fullName = formatName(
      client.firstName,
      client.lastName,
      NameFormat.NATURAL,
    ).toLowerCase();
    const familyName = client.family?.name?.toLowerCase() ?? '';
    return (
      fullName.includes(term) ||
      familyName.includes(term) ||
      (!isAdvisor && matchesAdvisorSearch(client.advisorUserId, advisorMap, term))
    );
  };

  const [debouncedSearch] = useDebouncedValue(searchTerm ?? '', 300);
  const serverFilters = { ...filters, search: debouncedSearch || undefined };

  return (
    <>
      <SimpleTable<ClientListItem>
        resource="clients"
        columns={columnsBase}
        variant="new-flow"
        enableSearch={false}
        onRowClick={client => {
          navigate(`/client/${client.id}/non-financial-data/personal-details`);
        }}
        {...(paginated
          ? {
              mode: 'server' as const,
              paginationType: 'infinite' as const,
              pageSize: 50,
              filters: serverFilters,
              initialSort: { columnAccessor: 'name', direction: 'asc' as const },
            }
          : {
              mode: 'client' as const,
              paginationType: 'infinite' as const,
              filters,
              externalSearchTerm: searchTerm,
              filterPredicate,
            })}
      />
      <ConfirmDeleteModal
        opened={!!clientToDelete}
        onClose={() => setClientToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete client"
        message={`Are you sure you want to delete ${formatName(clientToDelete?.firstName, clientToDelete?.lastName)}? This action cannot be undone.`}
        error={deletionError}
        isLoading={isDeletingClient}
      />
    </>
  );
}
